import { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { runWithHeldContentFade } from '../utils/pageCrossfade';

export type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'persianist_theme';
/** Previous app id; read once to migrate theme preference without losing user choice */
const LEGACY_THEME_STORAGE_KEY = 'persianLearningApp_theme';
const THEME_EVENT_NAME = 'persian-app:theme-changed';
/** Fired after a user toggle once the new theme is on screen. */
export const THEME_USER_CHANGED_EVENT = 'persian-app:theme-user-changed';

// Keep a simple in-memory cache so every hook instance stays in sync
let cachedTheme: Theme | null = null;
/** Drops a toast for an older toggle when the user switches again mid-fade. */
let themeToggleGeneration = 0;

const isAdminPage = () => window.location.pathname.startsWith('/admin');

const getInitialTheme = (): Theme => {
  if (cachedTheme) return cachedTheme;

  try {
    let stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored !== 'dark' && stored !== 'light') {
      const legacy = localStorage.getItem(LEGACY_THEME_STORAGE_KEY);
      if (legacy === 'dark' || legacy === 'light') {
        localStorage.setItem(THEME_STORAGE_KEY, legacy);
        localStorage.removeItem(LEGACY_THEME_STORAGE_KEY);
        stored = legacy;
      }
    }
    if (stored === 'dark' || stored === 'light') {
      cachedTheme = stored as Theme;
      return cachedTheme;
    }
  } catch {
    // Ignore localStorage errors
  }

  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    cachedTheme = 'dark';
    return 'dark';
  }

  cachedTheme = 'light';
  return 'light';
};

const applyThemeToDocument = (theme: Theme) => {
  const root = document.documentElement;
  const themeToApply = isAdminPage() ? 'light' : theme;

  if (themeToApply === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  root.setAttribute('data-theme', themeToApply);
};

const persistTheme = (theme: Theme) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    cachedTheme = theme;
  } catch {
    // Ignore persistence errors
  }
};

const broadcastTheme = (theme: Theme) => {
  window.dispatchEvent(new CustomEvent(THEME_EVENT_NAME, { detail: theme }));
};


/**
 * Custom hook for managing theme (light/dark mode)
 * Theme preference is cached in localStorage for persistence across sessions
 */
export const useTheme = () => {
  const [theme, setThemeState] = useState<Theme>(() => getInitialTheme());
  const effectiveTheme = isAdminPage() ? 'light' : theme;

  // Keep storage updated whenever the user's app-wide preference changes
  useEffect(() => {
    persistTheme(theme);
  }, [theme]);

  // Apply after every render so SPA route changes can force admin pages back to light.
  useEffect(() => {
    applyThemeToDocument(theme);
  });

  // Sync with navigation changes to keep admin pages forced to light
  useEffect(() => {
    const handlePopState = () => {
      setTimeout(() => applyThemeToDocument(theme), 0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [theme]);

  // Subscribe to cross-component/theme-change events so all hook instances stay in sync
  useEffect(() => {
    const handleThemeEvent = (event: Event) => {
      const detail = (event as CustomEvent<Theme>).detail;
      if (detail && detail !== theme) {
        setThemeState(detail);
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY && event.newValue) {
        const newTheme = event.newValue === 'dark' ? 'dark' : 'light';
        if (newTheme !== theme) {
          setThemeState(newTheme);
        }
      }
    };

    window.addEventListener(THEME_EVENT_NAME, handleThemeEvent as EventListener);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(THEME_EVENT_NAME, handleThemeEvent as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, [theme]);

  const updateTheme = useCallback((nextTheme: Theme) => {
    const normalized: Theme = nextTheme === 'dark' ? 'dark' : 'light';
    cachedTheme = normalized;
    persistTheme(normalized);
    applyThemeToDocument(normalized);
    broadcastTheme(normalized);
    return normalized;
  }, []);

  // Toggle between light and dark theme
  const toggleTheme = useCallback(() => {
    const current = cachedTheme === 'dark' || cachedTheme === 'light' ? cachedTheme : theme;
    const next: Theme = current === 'light' ? 'dark' : 'light';
    const generation = ++themeToggleGeneration;
    return runWithHeldContentFade(() => {
      flushSync(() => {
        updateTheme(next);
        setThemeState(next);
      });
    }, () => {
      if (generation !== themeToggleGeneration || isAdminPage()) return;
      window.dispatchEvent(new CustomEvent(THEME_USER_CHANGED_EVENT, { detail: next }));
    });
  }, [theme, updateTheme]);

  // Set theme explicitly
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(() => updateTheme(newTheme));
  }, [updateTheme]);

  return {
    theme: effectiveTheme,
    toggleTheme,
    setTheme,
    isDark: effectiveTheme === 'dark',
    isLight: effectiveTheme === 'light'
  };
};

